import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandMark, StatusBadge, buttonClassName } from '@argink/ui';
import { DashboardPreview } from '../components/home/DashboardPreview';
import { EvidenceTrailPreview } from '../components/home/EvidenceTrailPreview';
import { WhatsAppGlyph } from '../components/home/WhatsAppGlyph';
import { StageProduct, useStageState, type StageState } from '../components/home/StageProduct';
import atmosphere from '../assets/home/hero-atmosphere.webp';
import atmosphereMobile from '../assets/home/hero-atmosphere-mobile.webp';

/**
 * Home pública, direção de arte M3b.
 *
 * A versão anterior foi reprovada por ser um hero de SaaS genérico: tudo
 * centralizado, produto pequeno abaixo da dobra, copy com cara de landing
 * gerada. Esta refaz a arquitetura visual:
 *
 *  - o produto ocupa a primeira dobra e sangra pela borda direita, cortado;
 *  - a composição é assimétrica, com a coluna de texto estreita à esquerda;
 *  - o comprovante flutua SOBRE a janela do app, criando profundidade;
 *  - a superfície escura do hero é atravessada pelo produto, que entra na
 *    seção clara seguinte em vez de terminar junto com o fundo.
 *
 * Copy: frases curtas, concretas, sem travessão e sem antítese decorativa.
 */

/** Os três momentos que compartilham UMA superfície de produto. O produto fica
 * fixo e troca de estado conforme o texto avança, que é o que o mandato pede no
 * lugar de um mock novo por assunto. */
const STAGES: { id: StageState; eyebrow: string; title: string; body: string }[] = [
  {
    id: 'tracking',
    eyebrow: 'Acompanhamento',
    title: 'Acompanhe o que ainda falta',
    body: 'Veja quem já assinou, quem ainda está pendente e quais documentos precisam de atenção.',
  },
  {
    id: 'signer',
    eyebrow: 'Para quem assina',
    title: 'Para quem assina, o caminho é curto',
    body: 'O signatário abre o link, revisa o documento e assina. Sem criar uma conta.',
  },
  {
    id: 'methods',
    eyebrow: 'Formas de assinar',
    title: 'Escolha como o documento precisa ser assinado',
    body: 'Assinatura eletrônica para o dia a dia. Certificado digital ICP-Brasil quando o documento exigir assinatura qualificada.',
  },
];

const STAGE_IDS = STAGES.map((stage) => stage.id);

const PROCESS = [
  ['01', 'Enviar', 'Você define o documento e quem precisa assinar.'],
  ['02', 'Convidar', 'Cada participante recebe um convite com link próprio.'],
  ['03', 'Assinar', 'Ele abre, revisa e assina sem criar conta.'],
  ['04', 'Acompanhar', 'Você vê quem já assinou e quem ainda falta.'],
  ['05', 'Comprovar', 'No fim, uma trilha de evidências sustenta cada assinatura.'],
];

export function HomePage() {
  const [activeStage, registerStage] = useStageState(STAGE_IDS);

  return (
    <div className="ph">
      {/* Link de pulo e `<main>` vieram do `PublicShell`, que esta Home deixou de
        * usar por precisar de uma composição própria. Recriar a casca não pode
        * custar o atalho de teclado nem o marco de conteúdo: sem eles, quem navega
        * por teclado atravessa a navegação inteira em toda visita. */}
      <a className="ph-skip" href="#conteudo">
        Pular para o conteúdo
      </a>

      <SiteHeader />

      <main id="conteudo">
      <section className="ph-hero">
        <HeroAtmosphere />

        <div className="ph-hero__copy">
          <p className="ph-eyebrow">Assinatura de documentos</p>

          <h1 className="ph-hero__title">
            Documentos assinados, com <span>evidência</span> de cada assinatura.
          </h1>

          <p className="ph-hero__lead">
            Envie o documento, veja quem já assinou e guarde o comprovante.
          </p>

          <div className="ph-hero__actions">
            {/* CTA próprio da Home, não o primitive: o `primary` do design system é
              * fundo Ink com texto Paper, e sobre o hero Ink ele desaparece. Em vez
              * de sobrescrever a classe do package (acoplamento que `buttonClassName`
              * existe para evitar), a Home declara a sua própria ação dominante. */}
            <Link className="ph-cta" to="/login">
              Acessar plataforma
            </Link>
            <a className="ph-link" href="#como-funciona">
              Ver como funciona
            </a>
          </div>
        </div>

        <div className="ph-hero__product">
          <DashboardPreview />
          <div className="ph-hero__float">
            <EvidenceTrailPreview compact />
          </div>
        </div>
      </section>

      <section className="ph-process" id="como-funciona" aria-labelledby="ph-process-title">
        <div className="ph-process__head">
          <p className="ph-eyebrow ph-eyebrow--dark">Como funciona</p>
          <h2 id="ph-process-title" className="ph-process__title">
            Do envio à evidência, cada passo é visível.
          </h2>
        </div>

        <ol className="ph-rail">
          {PROCESS.map(([index, title, text]) => (
            <li key={index} className="ph-rail__step">
              <span className="ph-rail__index" aria-hidden="true">
                {index}
              </span>
              <span className="ph-rail__node" aria-hidden="true" />
              <h3 className="ph-rail__title">{title}</h3>
              <p className="ph-rail__text">{text}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* --------------------------------------------------- canvas contínuo */}
      <section className="ph-stage" aria-labelledby="ph-stage-title">
        <h2 id="ph-stage-title" className="argink-sr-only">
          O produto em cada etapa
        </h2>

        <StageNetwork />

        <div className="ph-stage__narrative">
          {STAGES.map((stage, index) => (
            <article
              key={stage.id}
              className="ph-moment"
              ref={(el) => registerStage(el, index)}
              data-active={activeStage === stage.id || undefined}
              id={stage.id === 'tracking' ? 'acompanhamento' : stage.id === 'methods' ? 'formas-de-assinar' : undefined}
            >
              <p className="ph-eyebrow ph-eyebrow--dark">{stage.eyebrow}</p>
              <h3 className="ph-moment__title">{stage.title}</h3>
              <p className="ph-moment__body">{stage.body}</p>
            </article>
          ))}
        </div>

        <div className="ph-stage__product">
          <div className="ph-stage__sticky">
            <StageProduct state={activeStage} />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- whatsapp */}
      <section className="ph-capability" aria-labelledby="ph-capability-title">
        <div className="ph-capability__inner">
          <p className="ph-eyebrow">
            <WhatsAppGlyph className="ph-capability__glyph" />
            Canal adicional
          </p>
          <h2 id="ph-capability-title" className="ph-capability__title">
            Convites e lembretes pelo WhatsApp
          </h2>
          <p className="ph-capability__body">
            Um recurso adicional para levar a solicitação de assinatura ao canal que o
            participante já utiliza.
          </p>
          {/* Honestidade obrigatória: não existe backend de WhatsApp no produto.
            * Nada aqui pode sugerir envio, entrega ou leitura. */}
          <p className="ph-capability__status">
            Ainda não está disponível. Hoje todo convite sai por e-mail.
            <StatusBadge tone="neutral">Recurso de plano superior</StatusBadge>
          </p>
        </div>

        {/* Presença para o canal, na metade da seção que estava vazia. É marca
          * d'água: escala grande, contraste baixo, sem contador, sem bolha de
          * mensagem nova e sem tela de conversa. Desenhar uma notificação aqui
          * afirmaria um tráfego que não existe — o texto ao lado diz, em todas as
          * letras, que o recurso não está disponível. */}
        <WhatsAppGlyph className="ph-capability__mark" />
      </section>

      {/* ------------------------------------------------------- evidências */}
      <section className="ph-evidence" id="evidencias" aria-labelledby="ph-evidence-title">
        <div className="ph-evidence__copy">
          <p className="ph-eyebrow">Evidências</p>
          <h2 id="ph-evidence-title" className="ph-evidence__title">
            Saiba exatamente o que sustenta cada assinatura
          </h2>
          <p className="ph-evidence__body">
            Participante, documento, horário, método e evidências ficam disponíveis para
            consulta. Os detalhes técnicos aparecem sob demanda.
          </p>
        </div>
        <div className="ph-evidence__visual">
          <EvidenceTrailPreview />
        </div>
      </section>

      {/* ------------------------------------------------------------- fecho */}
      <section className="ph-close" aria-labelledby="ph-close-title">
        <h2 id="ph-close-title" className="ph-close__title">
          Comece pelo próximo documento que precisa de assinatura.
        </h2>
        <Link className="ph-cta" to="/login">
          Acessar plataforma
        </Link>
      </section>
      </main>
    </div>
  );
}

function SiteHeader() {
  return (
    <header className="ph-nav">
      <Link to="/" className="ph-nav__brand" aria-label="ARGINK, página inicial">
        <BrandMark variant="lockup-dark" width={320} />
      </Link>

      <nav className="ph-nav__links" aria-label="Navegação principal">
        <a href="#como-funciona">Como funciona</a>
        <a href="#acompanhamento">Acompanhamento</a>
        <a href="#formas-de-assinar">Formas de assinar</a>
        <a href="#evidencias">Evidências</a>
      </nav>

      <Link className={buttonClassName({ variant: 'outline', size: 'sm', className: 'ph-nav__cta' })} to="/login">
        Acessar plataforma
      </Link>
    </header>
  );
}

/**
 * A rede do hero continuando pela superfície clara.
 *
 * Sem isto a página lia como duas peças empilhadas: uma cena escura iluminada em
 * cima e um documento claro embaixo. O §13 pede que a costura entre os momentos
 * seja intencional, e continuidade de rede é uma das formas que ele nomeia.
 *
 * Sobre Paper o traço precisa ser MUITO mais fraco que sobre Ink: medido na
 * tela, o alfa que era ambiente no escuro virou risco de caneta atravessando a
 * cena. Daí o contraste baixíssimo e um nó só — presença periférica, não desenho
 * a ser notado.
 *
 * E o traço ENTRA e se dissolve, em vez de atravessar a seção inteira. Esta
 * seção tem três viewports de altura; um SVG esticado sobre tudo isso vira uma
 * diagonal gigante e praticamente reta cruzando a tela de canto a canto — que é
 * exatamente o risco de caneta que se queria evitar. Ancorado no alto à direita
 * (`xMaxYMin`), ele continua a curva que vinha do hero e apaga logo abaixo, que
 * é o que costura os dois momentos.
 */
function StageNetwork() {
  return (
    <svg
      className="ph-stage__net"
      aria-hidden="true"
      viewBox="0 0 1200 620"
      preserveAspectRatio="xMaxYMin slice"
    >
      <defs>
        <linearGradient id="ph-stage-trace" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--argink-color-blue)" stopOpacity="0" />
          <stop offset="38%" stopColor="var(--argink-color-blue)" stopOpacity="0.13" />
          <stop offset="76%" stopColor="var(--argink-color-blue)" stopOpacity="0.07" />
          <stop offset="100%" stopColor="var(--argink-color-blue)" stopOpacity="0" />
        </linearGradient>
      </defs>

      <path
        d="M1180 -30 C 1010 150, 900 300, 610 560"
        fill="none"
        stroke="url(#ph-stage-trace)"
        strokeWidth="1.5"
      />
      <circle cx="946" cy="232" r="4" fill="var(--argink-color-blue)" opacity="0.22" />
    </svg>
  );
}

/**
 * Atmosfera do hero, em três camadas.
 *
 * A versão anterior era um único SVG de traços finos que, medido na tela,
 * praticamente sumia: linhas de 1px a 28% de opacidade sobre Ink não constroem
 * profundidade nenhuma (§14). A resposta não é acrescentar dezenas de pontos —
 * é dar ESCALA e LUZ a poucos elementos:
 *
 *  1. arte gerada (Higgsfield), que traz curva longa, névoa e nós — ver
 *     `PROVENANCE.md`. Entra por `<picture>` com variante mobile, e é só
 *     ambiente: não carrega informação nenhuma;
 *  2. luz volumétrica atrás do produto, em CSS, que é o que assenta a janela na
 *     cena em vez de deixá-la colada sobre um fundo chapado;
 *  3. um traço de rede curto e NÍTIDO em cima, com nós grandes o bastante para
 *     serem vistos — a assinatura visual da marca, não uma textura.
 *
 * Tudo `aria-hidden`: é ambiente, e nada aqui deve ser anunciado.
 */
function HeroAtmosphere() {
  return (
    <div className="ph-atmosphere" aria-hidden="true">
      <picture className="ph-atmosphere__art">
        <source media="(max-width: 44rem)" srcSet={atmosphereMobile} />
        {/* `alt` vazio porque a imagem é decorativa — descrevê-la seria ruído para
          * leitor de tela. Sem `fetchPriority`: a prop camelCase só existe a
          * partir do React 19, e no 18 ela vaza como atributo desconhecido e
          * emite warning no console a cada render. A imagem está na primeira
          * dobra e já é buscada com prioridade alta pelo próprio browser. */}
        <img src={atmosphere} alt="" decoding="async" />
      </picture>

      <div className="ph-atmosphere__glow" />

      <svg className="ph-atmosphere__net" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="ph-net-a" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--ph-trace-a)" stopOpacity="0" />
            <stop offset="55%" stopColor="var(--ph-trace-a)" stopOpacity="0.55" />
            <stop offset="100%" stopColor="var(--ph-signal)" stopOpacity="0.9" />
          </linearGradient>
          <radialGradient id="ph-node-halo">
            <stop offset="0%" stopColor="var(--ph-signal)" stopOpacity="0.55" />
            <stop offset="100%" stopColor="var(--ph-signal)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* UM traço, no quadrante do produto. A versão anterior cruzava o meio da
          * tela e passava por baixo da headline e do comprovante: os nós viravam
          * bolinhas soltas sobre o texto, competindo com a leitura em vez de
          * construir a cena. A rede pertence ao lado da luz. */}
        <path
          d="M660 742 C 840 640, 980 470, 1180 214"
          fill="none"
          stroke="url(#ph-net-a)"
          strokeWidth="2"
        />

        {/* Dois nós, grandes e com halo. Poucos e vistos, não muitos e invisíveis. */}
        <circle cx="742" cy="648" r="52" fill="url(#ph-node-halo)" />
        <circle cx="742" cy="648" r="4.5" fill="var(--ph-trace-a)" />
        <circle cx="1104" cy="300" r="78" fill="url(#ph-node-halo)" />
        <circle cx="1104" cy="300" r="6" fill="var(--ph-signal)" />
      </svg>
    </div>
  );
}
